/**
 * Approval Workflow React Query Hooks
 *
 * Custom hooks for managing approval workflow state and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { approvalService } from '@/services/approval.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { invalidateReleaseState } from '@/lib/invalidateRelease';
import {
  BulkUndoItem,
  ConfigureWorkflowRequest,
  AddCommentRequest,
  CommentAttachment,
  CommentVersionKey,
  RequestChangesRequest,
} from '@/types/approval';

// ===========================
// Query Keys (DEPRECATED - Use teamQueryKeys)
// ===========================

/**
 * @deprecated Use teamQueryKeys.approval.* instead
 * Kept for backward compatibility during migration
 */
export const approvalKeys = {
  all: ['approval'] as const,
  workflow: (teamId: number | null) => [...approvalKeys.all, 'workflow', teamId] as const,
  pendingApprovals: (teamId?: number | null) =>
    [...approvalKeys.all, 'pending', teamId || 'user'] as const,
  postApprovals: (postId: number) => [...approvalKeys.all, 'post', postId] as const,
  history: (postId: number) => [...approvalKeys.all, 'history', postId] as const,
  comments: (postId: number) => [...approvalKeys.all, 'comments', postId] as const,
};

// ===========================
// Query Hooks
// ===========================

/**
 * Hook to fetch approval workflow configuration for current team
 */
export const useApprovalWorkflow = (enabled = true) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.approval.workflow(teamId),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => approvalService.getWorkflowConfig(String(teamId!)),
    enabled: enabled && hasTeam,
  });
};

/**
 * Hook to fetch approvals for a specific post
 */
export const usePostApprovals = (postId: number, enabled = true) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.approval.post(teamId, postId),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => approvalService.getPostApprovals(String(postId)),
    enabled: enabled && hasTeam && !!postId,
  });
};

/**
 * Hook to fetch approval history for a post
 */
export const useApprovalHistory = (
  postId: number,
  enabled = true,
  pollIntervalMs?: number
) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.approval.history(teamId, postId),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => approvalService.getApprovalHistory(String(postId)),
    enabled: enabled && hasTeam && !!postId,
    refetchInterval: enabled && pollIntervalMs ? pollIntervalMs : false,
    refetchIntervalInBackground: false,
  });
};

/**
 * Hook to fetch the Release Board drawer payload (full media list, versions,
 * status, channels...). Keeps the previous version's data while a new
 * variation loads so the version switcher does not flicker.
 * Limited retries: the drawer offers a manual retry button instead.
 */
export const useReleaseDetail = (
  postId: number,
  variationId: number | null,
  enabled = true
) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.approval.releaseDetail(teamId, postId, variationId),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => approvalService.getReleaseDetail(postId, variationId),
    enabled: enabled && hasTeam && !!postId,
    placeholderData: (previous) => previous,
    retry: 1,
  });
};

export const useReviewDiff = (postId: number, variationId: number | null, enabled = true) => {
  const { teamId, hasTeam } = useEffectiveTeamId();
  return useQuery({
    queryKey: teamQueryKeys.approval.reviewDiff(teamId, postId, variationId),
    queryFn: () => approvalService.getReviewDiff(postId, variationId),
    enabled: enabled && hasTeam && !!postId,
    retry: 1,
  });
};

/**
 * Hook to fetch the comment thread of ONE version of a post
 * (variationId null/undefined = the Original).
 */
export const usePostComments = (
  postId: number,
  variationId: number | null | undefined,
  enabled = true
) => {
  const { teamId, hasTeam } = useEffectiveTeamId();
  const versionKey: CommentVersionKey = variationId ?? 'base';

  return useQuery({
    queryKey: teamQueryKeys.approval.comments(teamId, postId, versionKey),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => approvalService.getComments(String(postId), versionKey),
    enabled: enabled && hasTeam && !!postId,
    refetchInterval: enabled ? 15000 : false, // Poll every 15 seconds when panel is open
    refetchIntervalInBackground: false, // Stop polling when tab is not focused
  });
};

// ===========================
// Mutation Hooks
// ===========================

/**
 * Hook to configure approval workflow
 */
export const useConfigureWorkflow = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (config: ConfigureWorkflowRequest) =>
      approvalService.configureWorkflow(String(teamId!), config),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamQueryKeys.approval.workflow(teamId),
      });
      // Workflow changes alter every post's gating/pipeline state
      invalidateReleaseState(queryClient, { teamId });
    },
  });
};

/**
 * Hook to submit a post for approval
 */
export const useSubmitForApproval = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ postId, comment }: { postId: number; comment?: string }) =>
      approvalService.submitForApproval(String(postId), comment ? { comment } : undefined),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId });
    },
  });
};

/**
 * Hook to submit a variation for approval (resubmit after changes requested)
 */
export const useSubmitVariationForApproval = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ postId, variationId, comment }: { postId: number; variationId: number; comment?: string }) =>
      approvalService.submitVariationForApproval(postId, variationId, comment ? { comment } : undefined),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, variationId: variables.variationId });
    },
  });
};

/**
 * Hook to approve a post
 */
export const useApprovePost = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ approvalId, comment }: { approvalId: number; comment?: string }) =>
      approvalService.approve(String(approvalId), comment ? { comment } : undefined),
    onSuccess: () => {
      invalidateReleaseState(queryClient, { teamId });
    },
  });
};

/**
 * Hook to undo a release inside its undo window. Restores every release
 * surface (full-scope invalidation), also on failure (state may have moved on).
 */
export const useUndoRelease = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ postId, variationId }: { postId: number; variationId: number | null }) =>
      approvalService.undoRelease(postId, variationId),
    onSettled: (_data, _error, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId });
    },
  });
};

/**
 * Hook to undo a bulk release. Full-scope invalidation, also on failure (some items may have moved on).
 */
export const useUndoReleaseBulk = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (items: BulkUndoItem[]) => approvalService.undoReleaseBulk(items),
    onSettled: () => {
      invalidateReleaseState(queryClient, { teamId });
    },
  });
};

/**
 * Hook to request changes on a post
 */
export const useRequestChanges = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    // The message (with files and @mentions) is part of the decision: one call, one notification.
    mutationFn: ({
      approvalId,
      comment,
      mentionedUserIds,
      attachmentIds,
      parentId,
    }: { approvalId: number; postId: number } & RequestChangesRequest) =>
      approvalService.requestChanges(String(approvalId), { comment, mentionedUserIds, attachmentIds, parentId }),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId });
    },
  });
};

/**
 * Hook to fetch the people that can be @mentioned in a post's chat.
 * internal=true limits the list to the internal team.
 */
export const useMentionable = (postId: number | null, internal: boolean) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.approval.mentionable(teamId, postId ?? 0, internal),
    networkMode: 'always', // offline -> error + Retry, never paused forever
    queryFn: () => approvalService.getMentionable(postId!, internal),
    enabled: hasTeam && postId !== null,
    staleTime: 60_000,
  });
};

/**
 * Hook to add a comment to a post
 */
export const useAddComment = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ postId, ...data }: { postId: number } & AddCommentRequest) =>
      approvalService.addComment(String(postId), data),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, scope: 'comment' });
    },
  });
};

export interface UploadCommentAttachmentInput {
  postId: number;
  file: File;
  isVideo: boolean;
  /** 0-100 */
  onProgress: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload one chat attachment. Images/PDF: multipart POST. Video: presign, PUT
 * to storage (real progress), complete. Resolves with the pending attachment;
 * nothing to invalidate until the message is sent (useAddComment does that).
 */
export const useUploadCommentAttachment = () =>
  useMutation({
    mutationFn: async ({
      postId,
      file,
      isVideo,
      onProgress,
      signal,
    }: UploadCommentAttachmentInput): Promise<CommentAttachment> => {
      if (!isVideo) {
        return approvalService.uploadCommentAttachment(postId, file, onProgress, signal);
      }
      const presign = await approvalService.presignCommentAttachment(postId, file);
      try {
        await approvalService.putToSignedUrl(
          presign.uploadUrl,
          presign.uploadHeaders,
          file,
          (percent) => onProgress(Math.min(99, percent)),
          signal
        );
      } catch (error) {
        // Best effort: drop the orphaned pending row.
        void approvalService.deleteCommentAttachment(postId, presign.attachmentId).catch(() => undefined);
        throw error;
      }
      return approvalService.completeCommentAttachment(postId, presign.attachmentId);
    },
  });

/** Remove a pending (unsent) chat attachment. */
export const useDeleteCommentAttachment = () =>
  useMutation({
    mutationFn: ({ postId, attachmentId }: { postId: number; attachmentId: number }) =>
      approvalService.deleteCommentAttachment(postId, attachmentId),
  });

/**
 * Re-fetch the chat so expired signed attachment URLs are renewed.
 */
export const useRefreshCommentUrls = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  return (postId: number): void => {
    invalidateReleaseState(queryClient, { teamId, postId, scope: 'comment' });
  };
};

/**
 * Hook to delete a comment
 */
export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ commentId, postId: _postId }: { commentId: number; postId: number }) =>
      approvalService.deleteComment(String(commentId)),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, scope: 'comment' });
    },
  });
};

/**
 * Hook to update a comment
 */
export const useUpdateComment = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({
      commentId,
      content,
      postId: _postId,
    }: {
      commentId: number;
      content: string;
      postId: number;
    }) => approvalService.updateComment(String(commentId), content),
    onSuccess: (_, variables) => {
      invalidateReleaseState(queryClient, { teamId, postId: variables.postId, scope: 'comment' });
    },
  });
};
